import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadBase64 } from "@/lib/upload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

// Auto-select templates that read like closing documents.
const AUTO = /release|settlement|closing|disburse|authoriz/i;

export default function ClosingPacketDialog({ open, onOpenChange, clientId, clientName, onGenerated }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({});
  const [includeDisbursement, setIncludeDisbursement] = useState(true);
  const [saveToFile, setSaveToFile] = useState(true);
  const [title, setTitle] = useState("Settlement Closing Packet");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIncludeDisbursement(true); setSaveToFile(true);
    setTitle(`Settlement Closing Packet — ${clientName || ""}`.trim().replace(/—\s*$/, "").trim());
    api.get("/document-templates").then((r) => {
      setTemplates(r.data);
      const pre = {};
      r.data.forEach((t) => { if (AUTO.test(t.name)) pre[t.id] = true; });
      setSelected(pre);
    }).catch(() => {});
  }, [open, clientName]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const chosenIds = Object.keys(selected).filter((id) => selected[id]);

  const build = async () => {
    if (!includeDisbursement && chosenIds.length === 0) {
      toast.error("Include the disbursement statement and/or at least one document");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/closing-packet`, {
        template_ids: chosenIds, include_disbursement: includeDisbursement,
        title, save_to_file: saveToFile,
      });
      downloadBase64(data.content_b64, data.content_type, data.filename);
      toast.success(data.saved ? "Closing packet generated & saved to client file" : "Closing packet generated");
      if (data.saved) onGenerated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not build packet");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="closing-packet-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-[#002FA7]" /> One-click closing packet
          </DialogTitle>
          <DialogDescription className="sr-only">Bundle the disbursement statement and selected documents into one signable PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Packet title</label>
            <input data-testid="packet-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          </div>

          <label className="flex items-center gap-2.5 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="packet-include-disbursement">
            <input type="checkbox" checked={includeDisbursement} onChange={(e) => setIncludeDisbursement(e.target.checked)} className="accent-[#002FA7] h-4 w-4" />
            <span className="text-sm">
              <span className="font-semibold text-gray-800">Settlement disbursement statement</span>
              <span className="block text-xs text-gray-500">Adds the current disbursement breakdown with a signature line.</span>
            </span>
          </label>

          <div>
            <p className="block text-sm font-semibold text-gray-700 mb-1.5">Include documents</p>
            {templates.length === 0 ? (
              <p className="text-xs text-gray-400">No templates yet. Create templates under Templates to add letters.</p>
            ) : (
              <div className="space-y-1.5 max-h-52 overflow-y-auto border border-gray-100 rounded-sm p-2" data-testid="packet-template-list">
                {templates.map((t) => (
                  <label key={t.id} data-testid={`packet-template-${t.id}`}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={!!selected[t.id]} onChange={() => toggle(t.id)} className="accent-[#002FA7] h-4 w-4" />
                    <span className="text-sm text-gray-800">{t.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="packet-save-toggle">
            <input type="checkbox" checked={saveToFile} onChange={(e) => setSaveToFile(e.target.checked)} className="accent-[#002FA7]" />
            Save to client documents
          </label>
        </div>
        <DialogFooter className="pt-2">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={build} disabled={busy} data-testid="packet-build-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Build packet
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
